// Overview — the district dashboard landing page.
//
// Answers "is my investment being used, and is it working?" in one glance:
//   1. a summary band — the hero sentence and the stats that support it on one
//      surface, deliberately not a headline plus a separate KPI grid, which
//      would spend ~280px of the fold to state two numbers twice,
//   2. a "needs attention" digest — one conclusion per concern, each with a spark
//      preview and a link to the page where you act on it.
//
// The digest sits DIRECTLY under the band — keep it there. Any large panel
// wedged between them (~400px of chart) pushes all three conclusions below the
// fold and turns a landing page into one that opens on analysis rather than on
// findings. The big analytical panels live on the pages they belong to: the
// engagement bars are the Engagement tab's own hero and the cumulative mastery
// line is Learning's, both with the controls that make them explorable. A
// landing page's job is to say what needs looking at and hand you off.
//
// COPY BUDGET — each card is a status, a number, a spark, and ONE short sentence.
// The spark names the offenders (labelled rows, hover titles), so the sentence
// must not re-list them — a three-clause sentence next to a spark reads as a wall
// of text. If a fact only fits by adding a clause, it belongs on the destination
// page.
//
// TENSE — the whole page reads either present or retrospective, keyed off
// `isCurrent` (is the selected school year still running?). This is not a copy
// detail: a completed year's last bucket is July, so any "latest bucket" read on
// a past year reports summer — "▲ 1 vs last month" against 3,227 learners,
// "Robots active this month: 0 of 946", a fleet card claiming school is out of
// session. For a completed year those reads must be whole-window retrospectives
// instead of latest-bucket ones.
//
// RECENCY — and for the CURRENT year, "latest bucket" was never the right read
// either. The last bucket is the calendar month clipped to today, so it holds one
// day of data on the 1st and a full month on the 31st: the same district read
// "+0 standards this month · 0 of 946 robots active" and then, overnight, read
// healthy. Both cards now come off a trailing four-week window computed
// independently of the display bucketing (`orgRecent`, `recentState`), because
// "is this being used now" is a recency question and a calendar period cannot
// answer it. The buckets stay calendar months — they are comparable to each
// other, they align with the mastery snapshots, and they are how districts talk
// about their year.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FA from "../fa";
import {
  QuadSpark,
  CompositionSpark,
  FleetSpark,
  SparkLegend,
  ToneChip,
} from "../impact/primitives";
import { RailStat, StatRail } from "../impact/ui";
import {
  STATUS,
  TONES,
  worstTone,
  INK,
  MUTED,
  SURFACE,
  CARD_BORDER,
  CARD_RADIUS,
  CONTEXT,
  MASTERY_BANDS,
  UTIL_STATES,
} from "../impact/tokens";
import {
  MIN_OUTLIER_N,
  OUTLIER_SIGMA,
  median,
  robustScale,
} from "../impact/model";
import { rowTone } from "../impact/status";
import { syLabel } from "../impact/schoolyear";
import { isAllOrgs } from "../impact/chrome";
import {
  gradeLabel,
  fmtBucketLabel,
  fmtRecentSpan,
  withParam,
} from "../impact/format";
import {
  IDLE_FLAG_LABEL,
  isExpired,
  isIdleFlagged,
  recentState,
} from "../impact/fleet";

// Same surface as ui.js's Card. Kept as a spreadable style object rather than
// the component because these panels each layer their own flex/margin rules on
// top; the tokens are what has to stay shared.
const CARD = {
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: CARD_RADIUS,
  padding: 16,
  background: SURFACE,
};

const fmtInt = (v) => (v == null ? "—" : Math.round(v).toLocaleString());

// ---- summary band ----------------------------------------------------------
// The hero sentence plus the stats that support it, on one surface.
//
// The sentence is the line that gets read aloud in a board meeting, so it keeps
// display sizing and inline-emphasized numbers (proportional figures —
// tabular-nums looks loose at display sizes). What it does NOT need is a KPI
// grid restating the same two numbers 200px lower: the rail below carries only
// the facts the sentence leaves out — reach against the roster, depth per
// learner, recent movement, and fleet usage.
const Headline = ({ cur, year, isCurrent }) => {
  // An all-zero window takes the same copy as a missing one. A school year that
  // has only just begun does produce a series -- it just has nothing in it, and
  // "0 learners have mastered 0 standards so far in 2026–27" is a worse way to
  // say so. Engaged-but-zero-mastery still gets the sentence: that IS the news.
  if (!cur || (!cur.learnersInWindow && !cur.masteredInWindow))
    return (
      <div style={{ fontSize: 20, fontWeight: 400, color: MUTED }}>
        No mastery activity {isCurrent ? "recorded yet " : "recorded "}for the{" "}
        {syLabel(year)} school year.
      </div>
    );
  const Num = ({ children }) => (
    <strong style={{ fontWeight: 750, color: INK }}>{children}</strong>
  );
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 24,
        lineHeight: 1.3,
        fontWeight: 400,
        color: "#52514e",
      }}
    >
      <Num>{fmtInt(cur.learnersInWindow)}</Num> learners{" "}
      {isCurrent ? "have mastered" : "mastered"}{" "}
      <Num>{fmtInt(cur.masteredInWindow)}</Num> standards{" "}
      {isCurrent ? "so far in" : "in"} {syLabel(year)}.
    </h2>
  );
};

// RailStat / StatRail live in ui.js — the curriculum tab's summary strip is the
// same component, and one strip in two places should not be two components.

// The two tense-sensitive rail slots (see the TENSE note at the top):
//   movement — current year: what the latest bucket added. A completed year has
//              no "latest" worth reporting (it's July), so it reports the year's
//              busiest bucket instead.
//   fleet    — current year: how much of the fleet is delivering lessons right
//              now. A completed year reports how much of it delivered lessons at
//              any point during the year.
const movementStat = ({
  recent,
  busiest,
  compLoading,
  isCurrent,
  utilInterval,
}) => {
  if (!isCurrent)
    return {
      label: `Busiest ${utilInterval || "month"}`,
      value: busiest ? busiest.label : null,
      // "no engagement recorded" is a finding; "not here yet" is not. The mix
      // this reads is fetched outside the container's first-paint barrier, so
      // this slot is on screen before its data and has to say which it is.
      sub: busiest
        ? `${fmtInt(busiest.n)} learners engaged`
        : compLoading
        ? "loading…"
        : "no engagement recorded",
    };
  // The recency block, not the last two boundaries of the series (see RECENCY
  // above). Absent only on a payload that predates it, which is a "not
  // available" rather than a zero.
  if (!recent)
    return {
      label: "Recently",
      value: null,
      sub: "recent movement unavailable",
    };
  const span = fmtRecentSpan(recent.days);
  return {
    // The span IS the label, so the number stays qualified by what it was
    // measured over wherever this card gets screenshotted.
    label: span.charAt(0).toUpperCase() + span.slice(1),
    value: `+${fmtInt(recent.mastered)}`,
    // Mastery counts are cumulative states, so this is a gain and never negative
    // — presenting it as an up/down delta would imply a comparison that can't
    // happen. The learner count is a distinct count over the same span rather
    // than a difference of two counts (a returning learner would cancel out).
    sub: `standards · ${fmtInt(recent.learners)} learners engaged`,
  };
};

// The DENOMINATOR is tense-sensitive too, and unlike the numerator nothing on
// screen says so: a live year counts only robots under subscription, a finished
// one counts the fleet as it stood, so flipping between two adjacent years moves
// the base by however many subscriptions have lapsed since -- 704 of 2,705
// across all districts on prod, and 155 of 184 on Sumter, which reads as robots
// vanishing. The `sub` line is spent on the numerator's span, so the explanation
// hangs off the value as a hint (see RailStat).
//
// ONE sentence, saying what the base is. The rationale behind each tense
// ("this is what you are currently paying for") is not what the reader is stuck
// on -- they are stuck on what the number counts -- and a tooltip that argues
// its case is one nobody finishes reading.
//
// Emitted only when there is something to explain: with no lapsed robots the two
// denominators agree, and a hint saying "excludes 0 robots" is noise with a
// dotted underline on it.
const fleetDenominatorHint = ({ fleet, isCurrent, year }) => {
  if (!fleet.expired) return undefined;
  return isCurrent
    ? `Out of ${fmtInt(fleet.deployed)} robots under an active subscription.`
    : `Out of the ${fmtInt(fleet.deployed)} robots deployed during ${syLabel(
        year
      )}, including ${fmtInt(fleet.expired)} whose subscription has lapsed since.`;
};

const fleetStat = (props) => {
  const { fleet, isCurrent, year } = props;
  if (!fleet) return { label: "Fleet", value: null, sub: "utilization unavailable" };
  const hint = fleetDenominatorHint(props);
  if (!isCurrent)
    return {
      label: "Robots used",
      value: `${fmtInt(fleet.everActive)} of ${fmtInt(fleet.deployed)}`,
      sub: `delivered lessons in ${syLabel(year)}`,
      hint,
    };
  const span = fmtRecentSpan(fleet.recentDays);
  return {
    label: "Robots active",
    value: `${fmtInt(fleet.active)} of ${fmtInt(fleet.deployed)}`,
    // `active` is a four-week read now (see RECENCY), so the sub says over what
    // span: "this month" beside a number that is not a calendar month is the
    // same mislabelling in the other direction.
    sub: fleet.currentOOS
      ? `${span} · school is out of session`
      : `delivered lessons in the ${span}`,
    hint,
  };
};

const SummaryBand = (props) => {
  const { cur, orgRoster, isCurrent, year } = props;
  const reachPct =
    cur && orgRoster > 0
      ? `${((100 * cur.learnersInWindow) / orgRoster).toFixed(0)}%`
      : null;
  const stats = [
    {
      label: "Reach",
      value: reachPct,
      sub: orgRoster != null ? `of ${fmtInt(orgRoster)} enrolled` : "roster unavailable",
    },
    {
      label: "Per learner",
      value: cur ? cur.perLearnerInWindow.toFixed(1) : null,
      // states the denominator, which is not the roster: depth is averaged over
      // learners who actually used ABii, so it doesn't move when reach does
      sub: "standards, among the engaged",
    },
    movementStat(props),
    fleetStat(props),
  ];
  return (
    <div style={{ ...CARD, padding: "18px 20px 16px", marginBottom: 20 }}>
      <Headline cur={cur} year={year} isCurrent={isCurrent} />
      <StatRail divided>
        {stats.map((s) => (
          <RailStat key={s.label} {...s} />
        ))}
      </StatRail>
    </div>
  );
};

// ---- needs-attention digest --------------------------------------------------
// One card per concern: a tone chip (with label, never color alone), the count(s),
// a one-line conclusion, and the link to the page where you act on it.
//
// The card's TITLE is the name of the page it links to — Adoption, Learning,
// Fleet, the same four words the nav carries. Naming the card after its subject
// instead (Schools / Curriculum / Robots) would leave the landing page offering
// three findings and four destinations with no shared vocabulary between them: a
// reader who wants more about "Curriculum" would have to work out that the page
// for it is called Learning. The subject belongs in the stat captions — a caption
// has to say what its number counts.
//
// `stats` is a list because the fleet card carries TWO counts (robots to review,
// subscriptions) — they share one destination and one tone, so two cards for them
// would mean a duplicated link and a duplicated chip.
//
// Each stat may carry `of`, the whole its count was drawn from, rendered as a
// recessive "of M" suffix. It is what keeps a digest of exception counts from
// reading as the whole story: "9 units with gaps" is a bare alarm, "9 of 76" says
// in the same breath that 67 are fine. Set it only where the count really is a
// subset of that number — a denominator that doesn't contain its numerator is
// worse than none.
const fmtCount = (c) =>
  c == null ? "—" : typeof c === "number" ? c.toLocaleString() : c;

// Chip for the two states that are not verdicts: the fetch is still in flight, or
// it landed with nothing to judge. Both are deliberately NOT folded into the tone
// scale — a card that says "All clear" while its request is outstanding, or when
// the window has no data in it, is a claim we haven't earned. Neutral gray, so it
// never competes with the cards that did reach a verdict.
const NeutralChip = ({ label, spin }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 700,
      color: MUTED,
      background: "rgba(137, 135, 129, 0.10)",
      borderRadius: 999,
      padding: "3px 9px 3px 7px",
      whiteSpace: "nowrap",
    }}
  >
    {spin ? <FA color={MUTED} icon="spinner" spin /> : null}
    {label}
  </span>
);

const DigestCard = ({ tone, stats, title, detail, to, cta, loading, figure }) => {
  return (
    <div style={{ ...CARD, display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        {/* Sized and weighted as the card's own heading. It was 12px MUTED — the
            treatment this dashboard gives a stat caption — so the loudest text on
            each card was its number and the second loudest was the CTA link, and
            the word naming the card came third. */}
        <span style={{ color: INK, fontSize: 15, fontWeight: 700 }}>{title}</span>
        {loading ? (
          <NeutralChip label="Checking…" spin />
        ) : tone ? (
          <ToneChip tone={tone} />
        ) : (
          <NeutralChip label="No data" />
        )}
      </div>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
        {stats.map((st) => (
          // the hint qualifies this number, so it hangs off the number's own
          // wrapper (caption included) rather than the card
          <div key={st.caption || "only"} title={loading ? undefined : st.hint}>
            <div style={{ fontSize: 28, fontWeight: 650, color: INK, lineHeight: 1.1 }}>
              {loading ? "—" : fmtCount(st.count)}
              {/* The whole the exception count came out of, when the card knows
                  it. Same "N of M" grammar as the summary band's rail stats, so
                  the page has one way of saying part-of-whole -- and it costs no
                  vertical space and no clause of the one-sentence budget.
                  Recessive (rail-caption size, MUTED) because the exception
                  count is still the finding: at matched size "9 of 76" reads as
                  a single wide number and the loud half stops being loud.
                  Suppressed when the count isn't a number -- "— of 76" reads as
                  a rendering failure rather than as "can't tell yet". */}
              {!loading && st.of != null && typeof st.count === "number" && (
                <span style={{ fontSize: 15, fontWeight: 600, color: MUTED }}>
                  {" "}
                  of {fmtCount(st.of)}
                </span>
              )}
            </div>
            {/* Every stat carries its caption, single or not. The titles are one
                word now ("Curriculum", not "Curriculum gaps"), so the title no
                longer says what the number counts — the caption has to. */}
            {st.caption && (
              <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                {st.caption}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* the spark sits directly under the number it visualizes, above the
          sentence that names the offenders */}
      {!loading && figure && <div style={{ marginTop: 2 }}>{figure}</div>}
      <div style={{ fontSize: 13, color: "#52514e", flex: 1 }}>
        {loading ? "Checking…" : detail}
      </div>
      {/* No `to` means the concern page this card hands off to isn't served in
          the current scope (the all-organizations view serves the Overview
          alone). The card still carries the finding -- it is the conclusion that
          matters -- but a link onto a page that would bounce straight back is
          worse than no link, so the hand-off is dropped rather than dangled. */}
      {to ? (
        <Link to={to} style={{ fontSize: 13, fontWeight: 600 }}>
          {cta} →
        </Link>
      ) : (
        <div style={{ fontSize: 12, color: MUTED }}>
          Pick a district to dig in
        </div>
      )}
    </div>
  );
};

// ---- curriculum gaps ---------------------------------------------------------
// The exception report for "what are students learning": units the district is
// not getting through. Read district-wide (grade=all) on purpose. The endpoint's
// grade scoping exists so a COHORT can be compared against on-level content;
// this card asks a different question — which units are learners not clearing —
// and a unit is a unit regardless of who sat down at it. A grade-scoped version
// here would need either a control (a digest card is a conclusion, not a control
// surface — and Learning already owns a grade picker) or a rule, and "largest
// cohort" is not "worst cohort": it would headline Grade 2's three stuck units
// while Grade 4 has nine.
//
// Coverage bar matches the full table's default so the two agree on what counts
// as enough data. Note it bites much less at grade=all than on one cohort —
// district-wide, most units clear 15 learners — so the *share* threshold below is
// what actually selects the exceptions.
const UNIT_MIN_LEARNERS = 15;
// A gap is a unit whose not-mastered share is unusual FOR THIS DISTRICT, not one
// that clears a fixed bar. Share is of learner×standard records (the same measure
// the payload's default "focus" sort uses), and its distribution turns out not to
// be portable — measured over 2025–26 at grade=all:
//
//   Blue Springs (NATL set):  76 covered units, median 13.1%, max 41.0%
//   Dillon (state set):       27 covered units, median 24.5%, max 77.8%
//
// A fixed "over half not mastered" bar flags 0 units in the first district and 2
// in the second, so it is dead on the larger one; a fixed 30% flags 6 and 10 but
// only by coincidence of calibration. Unit granularity differs per standard set,
// which moves records-per-unit and therefore the whole distribution — so the bar
// has to come from the district's own curriculum.
//
// This is the same statistic the Schools card uses for its outliers (median +
// OUTLIER_SIGMA robust scale, MAD-based so the outliers don't inflate their own
// threshold), which keeps one definition of "unusual" across the dashboard. It
// also follows the Renewal Brief's rule of benchmarking a district against itself
// rather than against our target. Observed: 9/76 and 2/27 units flagged.
const GAP_MIN_SHARE = 0.2;
// Widespread vs. localized: once this share of the covered curriculum is in the
// gap set it stops being a few hard units and becomes a district-wide problem.
const GAP_WIDESPREAD = 0.25;

const buildGaps = (unitData) => {
  if (!unitData || !unitData.units) return null;
  const covered = unitData.units
    .filter((u) => u.total > 0 && u.learners >= UNIT_MIN_LEARNERS)
    .map((u) => ({ ...u, share: u.not_mastered / u.total }));
  const shares = covered.map((u) => u.share);
  const typical = median(shares);
  const cut = typical + OUTLIER_SIGMA * robustScale(shares, typical);
  const gaps = covered
    // The absolute floor is a sanity guard, not the selector: in a district where
    // every unit lands under ~13% not-mastered, "unusual" would otherwise flag a
    // unit at 8% and call it a gap. Nothing is a curriculum gap at 8%.
    .filter((u) => u.share > cut && u.share > GAP_MIN_SHARE)
    .sort((a, b) => b.share - a.share);
  // Rolled-up composition across every covered unit. The spark pins this as its
  // top row: three unlabelled gap bars have no scale, and "62% not mastered"
  // only means something against what the district's curriculum usually looks
  // like. Same reference row the full table pins above its unit list.
  const all = MASTERY_BANDS.reduce(
    (acc, b) => ({
      ...acc,
      [b.key]: covered.reduce((a, u) => a + (u[b.key] || 0), 0),
    }),
    {}
  );
  // The district average, as the reference BAR states it: records-weighted across
  // the covered curriculum. Distinct from `typical` (the median unit) on purpose —
  // `typical` selects the gaps, because a median with a MAD scale is what makes
  // the threshold robust to the very outliers it's picking. But a composition bar
  // can only draw an aggregate, so the aggregate is what the rule and every
  // user-facing "district average" quote, and the two must be the same number:
  // drawing the rule at the median puts it a couple of percent off the reference
  // bar's own amber edge, which reads as a rendering bug, not as two statistics.
  const allTotal = covered.reduce((a, u) => a + u.total, 0);
  const allShare = allTotal ? all.not_mastered / allTotal : 0;
  return {
    covered: covered.length,
    // `typical` is deliberately NOT returned: it is the selection threshold's
    // input, and exposing it invited a second "district average" into the copy
    // that disagreed with the reference bar by a couple of percent.
    allShare,
    gaps,
    all,
    // no covered units at all is "we can't tell", not "all clear"
    thin: covered.length === 0,
  };
};

// ---- fleet + subscriptions verdicts -----------------------------------------
// Two concerns, one card: they share a destination (the Fleet page) and a fix
// conversation, so splitting them would duplicate the link and the severity chip.
// Hoisted out of the JSX because each has a tense branch on top of its severity
// ladder, and four levels of nested ternary inside a prop is unreadable.
//
// Each returns a `hint` as well as a `detail`: the caveats these numbers need
// ("the two counts overlap", the soon/already-lapsed split) are worth having but
// would make this the longest card of the three if spent on the sentence. They
// ride as hover titles on the numbers they qualify instead, the way DeltaChip
// carries its period — available on the number, not in the card's one line.
//
// TONE — the only `action` on this page comes out of `subsVerdict`, and that is
// the point of the scale: a subscription has a date on it and a thing someone has
// to do before that date. Everything the fleet reads is a recommendation. A dark
// robot is a strong one (`attention`), but the cloud cannot tell a robot that
// broke from one that is boxed up in a closet over the summer, and "N robots to
// review" is on its face an invitation to look rather than an instruction. A past
// year never reaches `action` at all: nothing about a finished window can be
// acted on now.
const fleetVerdict = ({ fleet, isCurrent, year }) => {
  if (!fleet)
    return {
      tone: null,
      count: null,
      detail: "Robot utilization unavailable for this window.",
    };
  // A past year gets a retrospective: "dark" is a latest-bucket state, and the
  // out-of-session suppression fires on every completed year (whose last bucket
  // is July). `neverActive` is window-scoped and needs no tense.
  if (!isCurrent)
    return {
      tone: fleet.neverActive
        ? "attention"
        : fleet.idleFlagged
        ? "advisory"
        : "clear",
      count: fleet.needsLook,
      hint:
        `${fleet.neverActive} delivered no lessons all year; ${fleet.idleFlagged} ` +
        `were idle for ${IDLE_FLAG_LABEL} of in-session time in it. ` +
        `A robot can be both, so this is the union, not the sum.`,
      detail: fleet.needsLook
        ? `${fleet.neverActive} sat unused all year.`
        : `Every robot deployed in ${syLabel(year)} delivered lessons.`,
    };
  if (fleet.currentOOS)
    return {
      // school is out: dark robots are expected, so only the summer-excluded
      // idle streak can raise a flag
      tone: fleet.idleFlagged ? "advisory" : "clear",
      count: fleet.idleFlagged,
      hint:
        `Idle for ${IDLE_FLAG_LABEL} of in-session time before the break. ` +
        `${fleet.dark} are offline now, but school is out of session, so that ` +
        `isn't counted against them.`,
      // Keyed on the count this card actually headlines, not on `dark`: gated on
      // `dark` it explained an idle streak of zero robots ("0 to review" over
      // "Idle before the break"), because a summer fleet is nearly all dark.
      detail: fleet.idleFlagged
        ? `Idle for ${IDLE_FLAG_LABEL} of in-session time before the break.`
        : "School is out of session — being offline now isn't counted against the fleet.",
    };
  return {
    tone: fleet.dark ? "attention" : fleet.idleFlagged ? "advisory" : "clear",
    count: fleet.needsLook,
    hint:
      `${fleet.idleFlagged} idle ${IDLE_FLAG_LABEL}; ${fleet.dark} ` +
      `dark (not connecting). A robot can be both, so this is the union, not the sum.`,
    detail: fleet.needsLook
      ? `${fleet.dark} not connecting, ${fleet.idleFlagged} idle.`
      : "Every deployed robot delivered lessons recently.",
  };
};

// Renewal urgency ("expires within 60 days") is a fact about today, so it only
// belongs on the current year. On a past year the subscription question that
// fits the window is how much of the fleet lapsed inside it.
const subsVerdict = ({ fleet, isCurrent, year }) => {
  if (!fleet) return { tone: null, count: null };
  if (!isCurrent)
    return {
      // retrospective: the renewals either happened or didn't, months ago
      tone: fleet.lapsedInWindow ? "advisory" : "clear",
      count: fleet.lapsedInWindow,
      hint: `Robot subscriptions that ran out during ${syLabel(year)}.`,
    };
  return {
    // The one `action` on the page. Both branches are red because both name the
    // same required act — renew — and the difference between "must renew" and
    // "should have renewed" is urgency inside one obligation, not two tones.
    tone: fleet.expired || fleet.soon ? "action" : "clear",
    count: fleet.expired + fleet.soon,
    hint: `${fleet.soon} expire within 60 days; ${fleet.expired} already lapsed.`,
  };
};

// The gap card's spark: the worst units' mastery composition, full width, with a
// dashed rule marking where the district AVERAGE ends its mastered share.
//
// Unlabelled on purpose: a label column wide enough for a unit name still
// truncates it ("Gr 3 · Operations & Algebraic T…"), eating the words that tell
// two units apart while taking 40% of the card. Names ride on hover instead.
//
// The rule is what earns that: because these bars stack mastered-first, a unit's
// amber segment BEGINS at its mastered share, so the rule at the district average
// makes every bar's overshoot the visible measure of how much worse than average
// it is.
//
// The "all units" bar stays, above the rule's origin — the rule marks where that
// bar's own amber begins, so the two read as one statement rather than as a bar
// plus an unexplained line. It's differentiated by THICKNESS and a wider gap
// rather than by a label, the recessive treatment a reference gets everywhere else
// on this dashboard. Not by dimming: at 55% opacity its green and blue read as two
// more pale categories rather than as the same three, which is the one confusion a
// part-to-whole legend can't absorb.
const GAP_SPARK_ROWS = 5;
const GAP_BAR_MAX = 18; // per-bar ceiling — see the height note below
const GAP_BAR_MIN = 9;
const GAP_BAR_GAP = 6;
const GAP_REF_BAR_H = 8; // clearly thinner than any data bar, at any row count
const GapBars = ({ gaps, rows = GAP_SPARK_ROWS, height = 120 }) => {
  const pct = (v) => `${Math.round(100 * v)}%`;
  const list = gaps.gaps.slice(0, rows);
  // left edge of the reference bar's own not-mastered segment
  const rulePos = 100 * (1 - gaps.allShare);
  // `height` is a BUDGET, not a size: bars divide it, but capped. Dividing it
  // outright made a district with a single flagged unit draw one 120px slab —
  // a lone bar that size reads as a progress meter, not as one row of a
  // comparison, and it implied a magnitude that came from the row count. Capped,
  // bar thickness is constant and the block just gets shorter; the grid stretches
  // the three cards to a common height anyway, so nothing else moves.
  const barH = Math.min(
    GAP_BAR_MAX,
    Math.max(
      GAP_BAR_MIN,
      Math.floor(
        (height - GAP_BAR_GAP * list.length) / Math.max(list.length + 1, 1)
      )
    )
  );
  const bar = (u, key, ref) => (
    <div
      key={key}
      // Grade FIRST: it's the disambiguator when a unit name repeats across
      // grade levels.
      title={
        ref
          ? `All ${gaps.covered} units with enough coverage — ${pct(
              gaps.allShare
            )} not mastered on average`
          : `${u.level == null ? "" : `Gr ${gradeLabel(u.level)} · `}${u.unit}` +
            ` — ${pct(u.share)} not mastered (district average ${pct(
              gaps.allShare
            )})`
      }
      style={ref ? { marginBottom: GAP_BAR_GAP + 3 } : undefined}
    >
      <CompositionSpark row={u} height={ref ? GAP_REF_BAR_H : barH} />
    </div>
  );
  return (
    <div style={{ position: "relative", margin: "4px 0 8px" }}>
      {bar(gaps.all, "all", true)}
      <div style={{ display: "grid", gap: GAP_BAR_GAP }}>
        {list.map((u) => bar(u, `${u.unit_id}-${u.level}`, false))}
      </div>
      {/* drawn last so it sits over the fills */}
      <div
        style={{
          position: "absolute",
          top: -2,
          bottom: -2,
          left: `${rulePos}%`,
          borderLeft: `1px dashed ${INK}`,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

// ---- curriculum gap verdict --------------------------------------------------
// Under the copy budget this sentence gives up naming the units — the spark's
// rows already name them, with the grade prefix and a hover title, and re-listing
// the top two here costs a clause each plus their percentages. What the spark can't
// draw is the baseline those bars are unusual *against*, so that's all the
// sentence keeps: a reader who sees a long amber bar with no reference can't tell
// alarming from ordinary.
const gapVerdict = ({ gaps }) => {
  if (!gaps)
    return {
      tone: null,
      count: null,
      detail: "Unit mastery unavailable for this window.",
    };
  if (gaps.thin)
    return {
      // "we can't tell" is not "all clear" — NeutralChip, not a tone
      tone: null,
      count: "—",
      detail: `No unit reached ${UNIT_MIN_LEARNERS} learners — too little coverage to judge.`,
    };
  const n = gaps.gaps.length;
  const pct = (v) => `${Math.round(100 * v)}%`;
  return {
    // A curriculum gap is a teaching recommendation, never an obligation: the
    // widespread case is the one worth interrupting someone over, so it takes the
    // louder of the two recommendation tones rather than red.
    tone: n / gaps.covered > GAP_WIDESPREAD ? "attention" : n ? "advisory" : "clear",
    count: n,
    detail: n
      ? `Well above the district average of ${pct(gaps.allShare)} not mastered.`
      : `No unit stands out — the district averages ${pct(
          gaps.allShare
        )} not mastered.`,
  };
};

// Legend for the adoption spark. Derived from the dots actually on screen rather
// than written out, because every hand-written version of it has been wrong.
//
// It listed the three QUADRANT standings — Thriving / Reaching few / Needs support
// — in the quadrant palette, but the dots are colored by each row's verdict tone,
// and ~2/3 of rows carry a status kind whose tone is not their quadrant's. So a
// Bursty school in the low-mastery corner drew a blue dot beside a legend that
// said blue meant "Reaching few". And in outlier mode a single red "outlier"
// swatch stood in for a set of dots that are amber, blue and green.
//
// The WORDS come from the rows too, not from the tone scale. A legend reading
// "Needs a look / All clear" would be accurate about severity and still leave the
// card and the table it links to using two vocabularies for one color — the
// reader clicks an amber dot labelled "Needs a look" and lands on an amber pill
// labelled "Needs support". So each tone borrows the label the worklist will
// actually print for it: the most common one among the rows wearing that tone,
// which is a real label from a real row rather than a synthesized summary.
//
// Ordered by severity, and in outlier mode the gray "typical" entry goes last —
// it's the ground the flagged dots sit on, not a finding.
const LEGEND_TONES = ["attention", "advisory", "clear"];
const rowLabel = (p) =>
  p.kindLabel || (STATUS[p.status] && STATUS[p.status].label) || null;
const quadLegend = (quad, outlierMode) => {
  const byTone = new Map();
  (quad || [])
    .filter((p) => !outlierMode || p.isOutlier)
    .forEach((p) => {
      const t = rowTone(p);
      const label = rowLabel(p);
      if (!t || !label) return;
      if (!byTone.has(t)) byTone.set(t, new Map());
      const counts = byTone.get(t);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
  const items = LEGEND_TONES.filter((t) => byTone.has(t)).map((t) => ({
    // the modal label for this tone; ties break on the first seen, which is the
    // row order the worklist itself is sorted in
    label: [...byTone.get(t).entries()].sort((a, b) => b[1] - a[1])[0][0],
    color: TONES[t].color,
    round: true,
  }));
  if (outlierMode) items.push({ label: "typical", color: CONTEXT, round: true });
  return items;
};

const OverviewPage = ({
  api,
  org,
  base,
  search,
  comp,
  compLoading,
  orgSeries,
  orgRecent,
  orgRoster,
  robotUtil,
  quad,
  year,
  isCurrent,
  interval,
  utilInterval,
  start,
  end,
  standardset,
  noun,
}) => {
  // The all-organizations scope serves this page and no other, so the digest's
  // hand-off links are dropped (see DigestCard) -- the findings stay.
  const allOrgs = isAllOrgs(org);
  const series = orgSeries && orgSeries.length ? orgSeries : null;
  const cur = series ? series[series.length - 1] : null;
  // No `prev`. Differencing the last two boundaries is exactly the read the
  // RECENCY note above retired: those two boundaries are the current calendar
  // month clipped to today. `orgRecent` answers the movement question instead.

  // Fleet reads. Which robots count depends on the year in view: for the
  // CURRENT year the question is "what am I paying for and is it working", so
  // lapsed robots are out of scope; for a PAST year it is "what did we get out
  // of the fleet we had", and a robot whose subscription has since lapsed was
  // still in service back then — excluding it would shrink the denominator of a
  // finished year every time another subscription expires.
  const fleet = useMemo(() => {
    if (!robotUtil || !robotUtil.robots) return null;
    const all = robotUtil.robots;
    const robots = isCurrent ? all.filter((r) => !isExpired(r)) : all;
    // "Now" reads come off the trailing four-week window, not the last bucket
    // (see RECENCY at the top of this file, and fleet.recentState). This is the
    // half of the fix that had to land WITH the backend's out-of-session gate:
    // that gate used to fire on the sliver bucket and, in firing, suppressed the
    // "action needed" tone — so a fleet reading 100% dark looked calm. Fixing the
    // gate alone would have turned a wrong-but-quiet card into a wrong-and-loud
    // one.
    const states = robots.map(recentState);
    const deployed = states.filter((s) => s !== "pre").length;
    const active = states.filter((s) => s === "active").length;
    const dark = states.filter((s) => s === "dark").length;
    const inService = robots.filter((r) => recentState(r) !== "pre");
    // `idle_streak` counts BUCKETS, so the flag has to know the grain the series
    // was built at -- three quiet weeks is not three quiet months. See
    // fleet.isIdleFlagged.
    const flagged = (r) => isIdleFlagged(r, robotUtil.interval);
    const idleFlagged = inService.filter(flagged).length;
    // window-scoped, tense-free reads: lessons_total counts only lessons inside
    // the requested window, so these describe the year in view rather than now
    const everActive = inService.filter((r) => r.lessons_total > 0).length;
    const neverActive = inService.length - everActive;
    // Union, not sum. The digest headlines ONE count of robots worth looking at,
    // and its components overlap heavily: a robot that delivered nothing all
    // year necessarily has a maximal idle streak, and a dark robot usually has
    // one too. Summing them reported 1,093 problem robots in a 946-robot fleet.
    const needsLook = inService.filter((r) =>
      isCurrent
        ? recentState(r) === "dark" || flagged(r)
        : r.lessons_total === 0 || flagged(r)
    ).length;
    // renewal window: already expired (whole fleet), or expiring within 60 days
    const now = new Date();
    const soon = all.filter((r) => {
      if (!r.sub_end_date) return false;
      const days = (new Date(r.sub_end_date) - now) / 86400000;
      return days >= 0 && days <= 60;
    }).length;
    const expired = all.filter(isExpired).length;
    // Subscriptions that ran out *inside the window* — the retrospective
    // counterpart to soon/expired, which are both facts about today. Compared as
    // date strings against the window bounds, so trim any time component the
    // serializer emits (a bare `>= end` would drop a same-day expiry).
    const lapsedInWindow = all.filter((r) => {
      const d = r.sub_end_date && r.sub_end_date.slice(0, 10);
      return d && d >= start && d <= end;
    }).length;
    // How long the recency window actually covered, for labelling: it is clamped
    // to the window start, so it is shorter than four weeks early in a year.
    const recentDays = robotUtil.recent ? robotUtil.recent.days : null;
    // Is the fleet currently out of session (latest bucket is summer/break)?
    // When it is, robots being dark is expected -- we still show the count but
    // don't raise "action needed" on it; only the (summer-excluded) idle streak
    // flags a genuine problem.
    //
    // Forced false on a past year rather than left for callers to gate: a
    // completed year's last bucket is ALWAYS July, so an ungated read makes
    // every past year claim school is out of session, letting the fleet card
    // excuse itself on every historical view.
    //
    // Trustworthy now in a way it wasn't: the backend judges a bucket by its
    // nominal calendar span rather than by the part of it that has elapsed
    // (buckets.nominal_span), so the trailing bucket no longer reports summer for
    // the first three days of every month.
    const bkts = robotUtil.buckets || [];
    const currentOOS =
      isCurrent && bkts.length
        ? !!bkts[bkts.length - 1].out_of_session
        : false;
    // Per-bucket state counts for the spark. Derived from the robot rows rather
    // than a server rollup, exactly as the Fleet tab does it — same reason: the
    // merged per-OrgUnit fallback has no rollup to read. `pre` is deliberately
    // excluded from the stack, so bar height is the DEPLOYED fleet and the year's
    // rollout shows up as the strip growing rather than as a flat wall.
    const byBucket = bkts.map((b, i) => {
      const c = { active: 0, connected: 0, dark: 0 };
      robots.forEach((r) => {
        const s = r.states_by_bucket[i];
        if (s && s !== "pre") c[s] += 1;
      });
      return {
        ...c,
        oos: !!b.out_of_session,
        // Incomplete rather than out of session: a partial bucket's states are
        // real, they just cover less time than their neighbours, so nearly
        // everything in it reads dark and the strip would otherwise end in a
        // cliff that looks like a collapse.
        partial: !!b.partial,
        label: fmtBucketLabel(b.start, robotUtil.interval),
      };
    });
    return {
      byBucket,
      deployed,
      active,
      dark,
      idleFlagged,
      everActive,
      neverActive,
      needsLook,
      recentDays,
      soon,
      expired,
      lapsedInWindow,
      currentOOS,
    };
  }, [robotUtil, isCurrent, start, end]);

  // The year's peak engagement bucket — the retrospective stand-in for "what
  // this month added". Bands only, matching the hero bars' stacked total.
  const busiest = useMemo(() => {
    if (!comp || !comp.tiers || !comp.composition_by_bucket) return null;
    const bands = comp.tiers.filter((t) => t.kind === "band");
    let best = null;
    comp.composition_by_bucket.forEach((row, i) => {
      // A bucket the window cut short holds part of a month's engagement and is
      // not comparable to a full one. It can't win "busiest" — and on a past year
      // it can't lose it either, which is the case that made this worth guarding:
      // a completed school year's trailing bucket is a partial July.
      const bkt = (comp.buckets || [])[i];
      if (bkt && bkt.partial) return;
      const n = bands.reduce((a, t) => a + (row[t.key] || 0), 0);
      if (!best || n > best.n) best = { n, i };
    });
    if (!best || !best.n) return null;
    return {
      n: best.n,
      label: fmtBucketLabel(comp.buckets[best.i].start, comp.interval),
    };
  }, [comp]);

  // Unit mastery is a FOURTH endpoint and the Overview already blocks on three
  // (engagement has been observed at ~90s on the largest district), so this one
  // is fetched here rather than in the container's barrier: the page renders
  // immediately and the card fills itself in. `standardset` is left off unless
  // the URL overrode it — the view falls back to the organization's own
  // preferred set, which is the right answer for everyone but a superadmin
  // deliberately recomputing against another set.
  const [unitState, setUnitState] = useState({ loading: true });
  useEffect(() => {
    if (!org || !org.id) return undefined;
    let cancelled = false;
    setUnitState({ loading: true });
    api
      .fetchWindowedUnitMastery({
        // The all-orgs sentinel doubles as the server's scope name (see
        // impact/chrome's ALL_ORGS), so the scope and the id stay one fact.
        scope: isAllOrgs(org) ? "all" : "organization",
        id: org.id,
        grade: "all",
        start,
        end,
        standardset: standardset || undefined,
      })
      .then((d) => {
        if (!cancelled) setUnitState({ loading: false, gaps: buildGaps(d) });
      })
      .catch(() => {
        if (!cancelled) setUnitState({ loading: false, gaps: null });
      });
    return () => {
      cancelled = true;
    };
    // `api` is recreated every render by withAPI — depending on it never settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org && org.id, start, end, standardset]);
  const gaps = unitState.gaps;

  const outliers = (quad || []).filter((p) => p.isOutlier);
  const worstOutliers = [...outliers]
    .sort((a, b) => b.dist - a.dist)
    .slice(0, 2)
    .map((p) => p.name);
  const outlierMode = (quad || []).length >= MIN_OUTLIER_N;

  // Link builder. `extra` sets one param on top of the scope state already in the
  // URL — a digest card that names a lens has to arrive on the matching picture,
  // and it must not lose ?organization= / ?start= doing so.
  const to = (path, extra) => ({
    pathname: `${base}${path}`,
    search: extra ? withParam(search, extra[0], extra[1]) : search,
  });

  const fleetV = fleetVerdict({ fleet, isCurrent, year });
  const subsV = subsVerdict({ fleet, isCurrent, year });
  const gapV = gapVerdict({ gaps });

  return (
    <>
      <SummaryBand
        cur={cur}
        recent={orgRecent}
        orgRoster={orgRoster}
        fleet={fleet}
        busiest={busiest}
        compLoading={compLoading}
        year={year}
        isCurrent={isCurrent}
        utilInterval={utilInterval}
      />
      {/* No section heading. "Needs attention" was a label for a row that already
          announces itself — three cards, each with a severity chip and its own
          title — and on a past year it grew to "What needed attention in 2025–26",
          restating a school year the page header and the year picker both name. */}
      {/* Card order is deliberate: the curriculum card sits in the MIDDLE because
          its spark is the most legible of the three and "what aren't they
          learning" is the question this dashboard exists to answer. Robots move
          to the end — it's the operational concern, not the instructional one. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
        }}
      >
        <DigestCard
          title="Adoption"
          // Reads each row's TONE, not its raw quadrant class: the whole point of
          // the cascade is that a school below both medians might be Building (on
          // its way up, deliberately not flagged) or Under-resourced (a hardware
          // problem), and the digest should escalate on what the worklist will
          // actually say when the reader gets there.
          tone={
            !quad || !quad.length
              ? null
              : !outlierMode
              ? quad.some((p) => rowTone(p) === "attention")
                ? "advisory"
                : "clear"
              : outliers.length
              ? outliers.some((p) => rowTone(p) === "attention")
                ? "attention"
                : "advisory"
              : "clear"
          }
          stats={[
            {
              count:
                !quad || !quad.length
                  ? null
                  : outlierMode
                  ? outliers.length
                  : "—",
              // The title names the DESTINATION, so the caption carries the
              // subject — and it has to be the dimension noun, because this card
              // follows the global breakdown control (schools, teachers, grades,
              // engagement tiers) and "far from normal" alone says what was
              // measured but never what is counted.
              // Every row that had an impact series in this window -- the same
              // population the outlier threshold was computed over, and the same
              // dots the spark draws. Only meaningful in outlier mode; below
              // MIN_OUTLIER_N the count is "—" and the suffix suppresses itself.
              of: quad && quad.length ? quad.length : null,
              caption: `${noun.many} far from normal`,
              hint:
                `${noun.Many} more than ${OUTLIER_SIGMA} robust standard deviations from the district's median reach and mastery. ` +
                `Out of ${fmtInt((quad || []).length)} with activity in this window.`,
            },
          ]}
          detail={
            !quad || !quad.length
              ? `No ${noun.one} impact series for this window yet.`
              : !outlierMode
              ? `Too few ${noun.many} to flag outliers — review each directly.`
              : outliers.length
              ? `${worstOutliers.join(" and ")} ${
                  isCurrent ? "sit" : "sat"
                } farthest out.`
              : `No ${noun.one} ${isCurrent ? "stands" : "stood"} out from the district.`
          }
          figure={
            quad && quad.length ? (
              <>
                <QuadSpark points={quad} outlierMode={outlierMode} />
                <SparkLegend
                  items={quadLegend(quad, outlierMode)}
                  note="reach × mastery, vs the district median"
                />
              </>
            ) : null
          }
          // ?lens= explicitly, not by relying on Adoption's default: this card
          // draws the outlier scatter, so the click-through has to land on that
          // same picture even if the landing lens is ever changed.
          to={allOrgs ? null : to("/adoption", ["lens", "outliers"])}
          cta={`See which ${noun.many}`}
        />
        <DigestCard
          title="Learning"
          loading={unitState.loading}
          tone={gapV.tone}
          stats={[
            {
              count: gapV.count,
              // The covered curriculum, not every unit in the standard set: the
              // gap test only ran on units clearing UNIT_MIN_LEARNERS, so those
              // are the only ones that could have been flagged. The existing
              // hint already says so, which is why it needs no extra clause.
              of: gaps && !gaps.thin ? gaps.covered : null,
              caption: "units with gaps",
              hint: `Units whose not-mastered share is unusually high for this district, among those reaching ${UNIT_MIN_LEARNERS}+ learners.`,
            },
          ]}
          figure={
            gaps && !gaps.thin ? (
              <>
                <GapBars gaps={gaps} />
                <SparkLegend
                  items={MASTERY_BANDS.map((b) => ({
                    label: b.short,
                    color: b.color,
                  }))}
                  // the rule is the only unlabelled mark left in the spark, and
                  // without this the overshoot reads as decoration
                  note="dashed line = district average"
                />
              </>
            ) : null
          }
          detail={gapV.detail}
          // "By curriculum" is the Learning page's landing lens, so no ?lens=
          // is needed to arrive on the unit table this card is drawn from
          to={allOrgs ? null : to("/learning")}
          cta="See the curriculum"
        />
        <DigestCard
          title="Fleet"
          tone={worstTone(fleetV.tone, subsV.tone)}
          stats={[
            {
              count: fleetV.count,
              // No `of` here, unlike the other two cards. The summary band
              // directly above already states the fleet's size against a
              // utilization count ("Robots used 563 of 946"), so a second "of
              // 946" 100px lower is the KPI-grid duplication the band exists to
              // avoid. And the denominator only reads as good news when the
              // exception set is small -- "714 of 946" is a worse read than
              // "714", so the card would be paying for the repetition to make
              // itself sound worse.
              // the card is titled Fleet, so the caption has to name the unit
              // itself rather than leaving it at "to review"
              caption: "robots to review",
              hint: fleetV.hint,
            },
            {
              count: subsV.count,
              caption: isCurrent ? "subs to renew" : "subs lapsed",
              hint: subsV.hint,
            },
          ]}
          figure={
            fleet && fleet.byBucket.length ? (
              <>
                <FleetSpark buckets={fleet.byBucket} />
                <SparkLegend
                  items={["active", "connected", "dark"].map((k) => ({
                    // short forms: the tab's full labels ("Connected, no lessons")
                    // wrap this legend to three lines at card width
                    label: { active: "active", connected: "no lessons", dark: "dark" }[k],
                    color: UTIL_STATES[k].color,
                  }))}
                  note={`deployed robots each ${utilInterval || "month"}`}
                />
              </>
            ) : null
          }
          detail={fleetV.detail}
          to={allOrgs ? null : to("/fleet")}
          cta="Review the fleet"
        />
      </div>
    </>
  );
};

export { OverviewPage };
